import { useAccount } from '@src/contexts';
import useMedia from '@src/hooks/utils/useMedia';
import { getBusinessDocumentationSchema } from '../utils';
import { BusinessDocumentationFormData } from '../components/businessDocumentationStep';
import { useMemo } from 'react';
import { v4 } from 'uuid';
import ClientApi from '@src/types/client';
import { checkNAValues } from '@src/utils/checkNAValues';
import { businessTypeMap, countryMap } from '../data/businessDocumentation';
import { DOCUMENT_REQUIREMENTS } from '../data/businessDocumentsRequirements';

interface IUseBusinessDocumentationFormOptions {
  businessInformationDetails:
    | ClientApi.Onboarding.GetBusinessInformationDetails.Response['data']
    | undefined;
  agreementValue?: boolean;
}

const useBusinessDocumentationForm = ({
  businessInformationDetails,
  agreementValue = false
}: IUseBusinessDocumentationFormOptions) => {
  const { user, userAddress } = useAccount();
  const { isMobile } = useMedia();

  const rawCountry = userAddress?.country;
  const rawBusinessType = businessInformationDetails?.businessInfomation?.businessType; // e.g., 'Sole Proprietorship'

  const normalizedCountry = countryMap[rawCountry as string];
  const normalizedBusinessType = businessTypeMap[rawBusinessType as string];

  const requiredDocs =
    normalizedCountry &&
    normalizedBusinessType &&
    DOCUMENT_REQUIREMENTS[normalizedCountry]?.[normalizedBusinessType]
      ? DOCUMENT_REQUIREMENTS[normalizedCountry][normalizedBusinessType]
      : [];

  const validationSchema = getBusinessDocumentationSchema(requiredDocs);

  const initialValues: BusinessDocumentationFormData = useMemo(() => {
    const businessDocument = businessInformationDetails?.businessDocument;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseDocs: any = {
      certificateOfIncorporation: checkNAValues(
        businessDocument?.certificateOfIncorporation || null
      ),
      amlComplianceDocument: checkNAValues(businessDocument?.amlDocumentation || null),
      agentOnboardingKybForm: checkNAValues(businessDocument?.agentOnboardingKYBForm || null),
      otherComplianceDocs:
        businessDocument?.otherComplianceDocuments?.map((doc) => ({
          id: doc?.id || v4(),
          document: doc?.otherDocumentationType || null,
          documentName: doc.title || '',
          show: true
        })) || [],
      agreement: agreementValue,
      einCertificate: checkNAValues(businessDocument?.einCertificate || null),
      irsCertificate: checkNAValues(businessDocument?.irsCertificate || null),
      certificateOfGoodStanding: checkNAValues(businessDocument?.certificateOfGoodStanding || null),
      businessDba: checkNAValues(businessDocument?.businessDBA || null)
    };

    requiredDocs.forEach((doc) => {
      baseDocs[doc.name] = checkNAValues(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (businessInformationDetails?.businessDocument as any)?.[doc.name] || null
      );
    });

    return baseDocs;
  }, [businessInformationDetails, agreementValue, requiredDocs]);

  return { validationSchema, initialValues, isMobile, user, requiredDocs };
};

export default useBusinessDocumentationForm;
